import { db } from '../../config/firebase';
import { collection, getDocs, query, where, doc, setDoc, addDoc } from 'firebase/firestore';
import { logError, logInfo } from '../logger';
import Papa from 'papaparse';
import { SAMPLE_STRUCTURES, importSampleData as importSampleDataOriginal } from './importHauteursLibresData';

// Collection name for all structure data
const COLLECTION_NAME = 'hauteurs_libres'; // Single collection for all data

/**
 * CSV column mapping for hauteurs libres data
 * This maps the expected CSV column names to our internal field names
 */
const CSV_COLUMN_MAPPING = {
  'Référence': 'reference',

  'Route': 'route',

  'Sequence': 'sequence',

  'Municipalite': 'municipalite',
  'Obstacle': 'obstacle',
  'Direction SO': 'direction_SO',
  'Direction NE': 'direction_NE',
  'Hauteur SO': 'hauteur_SO',
  'Hauteur NE': 'hauteur_NE',
  'Hauteur': 'hauteur',
  'Latitude': 'latitude',
  'Longitude': 'longitude'
};

/**
 * Gets details for a specific structure by reference
 * @param {string} reference - Structure reference ID
 * @returns {Promise<Object>} - Structure details
 */
export const getStructureDetails = async (reference) => {
  if (!reference) {
    throw new Error('Structure reference is required');
  }
    
  try {
    // Get the main structure data
    const structureQuery = query(collection(db, COLLECTION_NAME), where('reference', '==', reference));
    
    const structureSnapshot = await getDocs(structureQuery);
    
    if (structureSnapshot.empty) {
      throw new Error(`Structure with reference ${reference} not found`);
    }
    
    const structureData = structureSnapshot.docs[0].data();
    
    // Return the structure data which already contains details and routes
    return structureData;
  } catch (error) {
    throw new Error(`Failed to get structure details: ${error.message}`);
  }
};

/**
 * Get hauteurs libres data with optional filters
 * @param {string} reference - Optional reference to filter by
 * @param {Object} filters - Additional filters for the query
 * @returns {Promise<Array>} - Array of structures
 */
export const getHauteursLibres = async (reference = null, filters = {}) => {
  try {
    let q = query(collection(db, COLLECTION_NAME));

    // If reference is provided, do a direct lookup
    if (reference) {
      q = query(q, where('reference', '==', reference));
    }

    // Apply other filters if provided
    if (filters.municipalite) {
      q = query(q, where('municipalite', '==', filters.municipalite));
    }
    if (filters.route) {
      q = query(q, where('route', '==', filters.route));
    }
    if (filters.minHauteur) {
      q = query(q, where('hauteur', '>=', parseFloat(filters.minHauteur)));
    }
    if (filters.maxHauteur) {
      q = query(q, where('hauteur', '<=', parseFloat(filters.maxHauteur)));
    }

    const querySnapshot = await getDocs(q);
    const structures = [];
    
    querySnapshot.forEach((doc) => {
      structures.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return structures;
  } catch (error) {
    throw new Error(`Failed to fetch structures: ${error.message}`);
  }
};

/**
 * Import structures from CSV data
 * @param {File|string} csvInput - CSV file or string content
 * @returns {Promise<{success: Array, failed: Array, detailsSuccess: Array, detailsFailed: Array}>}
 */
export const importHauteursLibres = async (csvInput) => {
  try {
    // Initialize results object
    const results = {
      success: [],
      failed: [],
      detailsSuccess: [],
      detailsFailed: []
    };

    // Parse CSV data
    let csvData;
    if (!csvInput) {
      throw new Error('No CSV data provided');
    } else if (typeof csvInput === 'string') {
      // If it's already a string, parse it directly
      logInfo('Parsing CSV from string input');
      csvData = await parseCSV(csvInput);
    } else if (csvInput instanceof File) {
      // If it's a File object, read it first
      logInfo(`Parsing CSV from file: ${csvInput.name}`);
      const fileContent = await readFileAsText(csvInput);
      csvData = await parseCSV(fileContent);
    } else if (Array.isArray(csvInput)) {
      // If it's already an array of objects, assume it's from a CSV
      logInfo('Using pre-parsed CSV data array');
      csvData = csvInput;
    } else {
      throw new Error('Invalid input: expected CSV file or CSV string content');
    }

    logInfo(`Successfully parsed ${csvData.length} structures from CSV data`);

    // Process each structure
    for (const structure of csvData) {
      try {
        // Generate a reference if missing
        if (!structure.reference) {
          structure.reference = `GEN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          logInfo(`Generated reference ${structure.reference} for structure without reference`);
        }

        // Normalize data
        const normalizedStructure = normalizeStructureData(structure);
        
        // Save to Firestore - all data in a single document
        await saveStructureToFirestore(normalizedStructure);
        
        results.success.push({
          reference: normalizedStructure.reference,
          obstacle: normalizedStructure.obstacle
        });
      } catch (error) {
        results.failed.push({
          reference: structure.reference || 'unknown',
          error: error.message
        });
      }
    }

    logInfo(`Import completed: ${results.success.length} structures imported, ${results.failed.length} failed`);
    return results;
  } catch (error) {
    logError('Failed to import structures:', error);
    throw error;
  }
};

/**
 * Parse CSV data to array of objects
 * @param {string} csvText - CSV text content from file or string
 * @returns {Promise<Array>} - Array of objects
 */
const parseCSV = (csvText) => {
  return new Promise((resolve, reject) => {
    logInfo('Starting CSV parsing with PapaParse');
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => {
        // Map CSV column names to our internal field names
        return CSV_COLUMN_MAPPING[header] || header;
      },
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          const errorMsg = `CSV parsing error: ${results.errors[0].message}`;
          logError(errorMsg);
          reject(new Error(errorMsg));
        } else {
          logInfo(`CSV parsing complete: ${results.data.length} rows parsed`);
          
          // Log the first row as a sample
          if (results.data.length > 0) {
            logInfo('Sample CSV row:', JSON.stringify(results.data[0]));
          }
          
          resolve(results.data);
        }
      },
      error: (error) => {
        const errorMsg = `CSV parsing error: ${error.message}`;
        logError(errorMsg);
        reject(new Error(errorMsg));
      }
    });
  });
};

/**
 * Read file as text
 * @param {File} file - CSV File object
 * @returns {Promise<string>} - File content as text
 */
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      logInfo(`CSV file read successfully: ${file.name} (${event.target.result.length} bytes)`);
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      logError(`Error reading CSV file: ${file.name}`, error);
      reject(error);
    };
    reader.readAsText(file);
  });
};

/**
 * Normalize structure data
 * @param {Object} structure - Raw structure data
 * @returns {Object} - Normalized structure data
 */
const normalizeStructureData = (structure) => {
  // Convert string numbers to actual numbers (use 0 if parsing fails)
  const parseFloatSafe = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  };
  
  const hauteur = parseFloatSafe(structure.hauteur) || 
                  parseFloatSafe(structure.hauteur_SO) || 
                  parseFloatSafe(structure.hauteur_NE);
  
  // Extract coordinates
  let coordinates = null;
  const lat = parseFloatSafe(structure.latitude);
  const lng = parseFloatSafe(structure.longitude);
  
  if (lat !== null && lng !== null) {
    coordinates = {
      latitude: lat,
      longitude: lng
    };
  }
  
  // Create normalized structure object
  const normalizedStructure = {
    reference: structure.reference,
    route: structure.route || null,
    sequence: structure.sequence || null,
    municipalite: structure.municipalite || null,
    obstacle: structure.obstacle || null,
    direction_SO: structure.direction_SO || null,
    hauteur_SO: parseFloatSafe(structure.hauteur_SO),
    direction_NE: structure.direction_NE || null,
    hauteur_NE: parseFloatSafe(structure.hauteur_NE),
    hauteur: hauteur,
    coordinates,
    latitude: lat,
    longitude: lng,
    // Store all original data to ensure nothing is lost
    originalData: { ...structure },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  // Add details if available
  if (structure.details) {
    normalizedStructure.details = structure.details;
  } else if (hauteur !== null) {
    // Create a basic detail from the main structure data
    normalizedStructure.details = [
      {
        point: "1",
        direction: structure.direction_SO || "N/A",
        gauche: hauteur,
        centre_gauche: hauteur,
        centre: hauteur,
        centre_droit: hauteur,
        droite: hauteur,
        remarque: "",
        date: new Date().toISOString().split('T')[0]
      }
    ];
  }
  
  // Add routes if available
  if (structure.routes) {
    normalizedStructure.routes = structure.routes;
  } else if (structure.route) {
    // Create a basic route from the main structure data
    normalizedStructure.routes = [
      {
        route: structure.route,
        sequence: structure.sequence,
        municipalite: structure.municipalite
      }
    ];
  }
  
  return normalizedStructure;
};

/**
 * Save structure to Firestore
 * @param {Object} structure - Normalized structure data
 * @returns {Promise<void>}
 */
const saveStructureToFirestore = async (structure) => {
  try {
    // Check if structure already exists
    const existingQuery = query(collection(db, COLLECTION_NAME), where('reference', '==', structure.reference));
    
    const existingSnapshot = await getDocs(existingQuery);
    
    if (!existingSnapshot.empty) {
      // Update existing structure
      const docRef = existingSnapshot.docs[0].ref;
      await setDoc(docRef, {
        ...structure,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      logInfo(`Updated existing structure ${structure.reference}`);
    } else {
      // Create new structure
      await addDoc(collection(db, COLLECTION_NAME), structure);
      logInfo(`Created new structure ${structure.reference}`);
    }
  } catch (error) {
    logError(`Failed to save structure ${structure.reference || 'unknown'}:`, error);
    throw error;
  }
};

/**
 * Re-export importSampleData from importHauteursLibresData.js
 */
export const importSampleData = importSampleDataOriginal;